
import { jitsiLocalStorage } from '@jitsi/js-utils';

import {
    DATA_CHANNEL_OPENED
} from '../base/conference';
import { SET_CONFIG } from '../base/config';
import { isMobileBrowser } from '../base/environment/utils';
import { getParticipantCount } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { checkIfReactNative } from '../base/util/checkOS';

import { setMaxReceiverVideoQuality, setPreferredVideoQuality } from './actions';
import { applyLocalCaptureConstraint } from './captureConstraint';
import { DEFAULT_PREFERRED_VIDEO_QUALITY, VIDEO_QUALITY_LEVELS } from './constants';
import logger from './logger';

declare var APP: Object;

/**
 * Implements the middleware of the feature video-quality.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    if (action.type === DATA_CHANNEL_OPENED) {
        return _syncReceiveVideoQuality(getState, next, action);
    }

    const result = next(action);

    switch (action.type) {
    case SET_CONFIG: {
        const state = getState();
        const checked = jitsiLocalStorage.getItem('features/video-quality-checked') === 'true';
        const persistedPrefferedVideoQuality = state['features/video-quality-persistent-storage']?.persistedPrefferedVideoQuality;

        if (!checked || typeof persistedPrefferedVideoQuality === 'undefined') {
            dispatch(setPreferredVideoQuality(DEFAULT_PREFERRED_VIDEO_QUALITY));

        } else {
            dispatch(setPreferredVideoQuality(persistedPrefferedVideoQuality));
        }

        break;
    }
    }

    return result;
});

/**
 * Helper function for updating the preferred receiver video constraint, based
 * on the user preference and the internal maximum.
 *
 * @param {MHConference} conference - The MHConference instance for the
 * current call.
 * @param {number} preferred - The user preferred max frame height.
 * @param {number} max - The maximum frame height the application should
 * receive.
 * @returns {void}
 */
function _setReceiverVideoConstraint(conference, preferred, max) {
    if (conference) {
        const value = Math.min(preferred, max);

        conference.setReceiverVideoConstraint(value);
        logger.info(`setReceiverVideoConstraint: ${value}`);
    }
}

/**
 * Helper function for updating the preferred sender video constraint, based
 * on the user preference.
 *
 * @param {MHConference} conference - The MHConference instance for the
 * current call.
 * @param {number} preferred - The user preferred max frame height.
 * @param {Object} [store] - The redux store, needed to narrow the capture itself.
 * @returns {void}
 */
function _setSenderVideoConstraint(conference, preferred, store) {
    if (!conference) {
        return;
    }

    const applyEncodingConstraint = () =>
        conference.setSenderVideoConstraint(preferred)
            .catch(err => {
                logger.error(`Changing sender resolution to ${preferred} failed - ${err} `);
            });

    if (!store) {
        applyEncodingConstraint();

        return;
    }

    // `setSenderVideoConstraint` only rewrites the RTP encodings, so on its own the camera keeps capturing at full
    // resolution and the local preview - which renders the capture track, not the encoded stream - never follows
    // the selected quality. Narrow the capture first so the preview matches, then let the encodings be recomputed
    // against the resolution now actually being captured; `getCaptureResolution()` feeds the simulcast scale
    // factors, so the other order would size every layer off a stale number.
    //
    // This does not lower what remote participants can receive: `setSenderVideoConstraint(preferred)` already
    // capped the send at the same value, so the ceiling is unchanged and only the wasted capture-and-downscale
    // work goes away.
    //
    // Always resolves - a camera that refuses the mode leaves the encoding-level cap in force, which is exactly
    // what happened before this existed. It must never fail the quality change, and this is a redux listener.
    applyLocalCaptureConstraint(store, preferred).then(applyEncodingConstraint, applyEncodingConstraint);
}

/**
 * Sets the maximum receive video quality.
 *
 * @param {Function} getState - The redux function which returns the current redux state.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code DATA_CHANNEL_STATUS_CHANGED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _syncReceiveVideoQuality(getState, next, action) {
    const state = getState();
    const {
        conference
    } = state['features/base/conference'];
    const {
        maxReceiverVideoQuality = VIDEO_QUALITY_LEVELS.HIGH,
        preferredVideoQuality
    } = state['features/video-quality'] ?? {};

    _setReceiverVideoConstraint(
        conference,
        preferredVideoQuality,
        maxReceiverVideoQuality);

    return next(action);
}


/**
 * Registers a change handler for state['features/base/conference'] to update
 * the preferred video quality levels based on user preferred and internal
 * settings.
 */
StateListenerRegistry.register(
    /* selector */ state => {
        const { conference } = state['features/base/conference'];
        const {
            maxReceiverVideoQuality = VIDEO_QUALITY_LEVELS.HIGH,
            preferredVideoQuality
        } = state['features/video-quality'] ?? {};

        return {
            conference,
            maxReceiverVideoQuality,
            preferredVideoQuality
        };
    },
    /* listener */ (currentState, store, previousState = {}) => {
        const {
            conference,
            maxReceiverVideoQuality = VIDEO_QUALITY_LEVELS.HIGH,
            preferredVideoQuality
        } = currentState;
        const changedConference = conference !== previousState.conference;
        const changedPreferredVideoQuality = preferredVideoQuality !== previousState.preferredVideoQuality;
        const changedMaxVideoQuality = maxReceiverVideoQuality !== previousState.maxReceiverVideoQuality;

        if (changedConference || changedPreferredVideoQuality || changedMaxVideoQuality) {
            _setReceiverVideoConstraint(conference, preferredVideoQuality, maxReceiverVideoQuality);
        }
        if (changedConference || changedPreferredVideoQuality) {
            _setSenderVideoConstraint(conference, preferredVideoQuality, store);
        }

        if (typeof APP !== 'undefined' && changedPreferredVideoQuality) {
            APP.API.notifyVideoQualityChanged(preferredVideoQuality);
        }
    });

StateListenerRegistry.register(
    /* selector */ state => {
        const participantCount = getParticipantCount(state);
        const tileViewTimeLimit = state['features/filmstrip']?.tileViewTileLimit;
        const finalParticipantCount = participantCount > tileViewTimeLimit ? tileViewTimeLimit : participantCount;
        const savePreferenceEnabled = jitsiLocalStorage.getItem('features/video-quality-checked') === 'true';
        const userPreferredQuality = savePreferenceEnabled ? state['features/video-quality-persistent-storage'] : undefined;
        const config = state['features/base/config'];

        return {
            finalParticipantCount,
            userPreferredQuality,
            maxReceiverVideoQuality: state['features/video-quality'].maxReceiverVideoQuality,
            preferredVideoQuality: state['features/video-quality'].preferredVideoQuality,
            preferredVideoQualityUserInitiated:
                state['features/video-quality'].preferredVideoQualityUserInitiated,
            pregData: state['features/base/mtApi']?.pregData ?? {},
            config
        };
    },
    /* listener */ ({
        finalParticipantCount,
        userPreferredQuality,
        maxReceiverVideoQuality,
        preferredVideoQuality,
        preferredVideoQualityUserInitiated,
        pregData,
        config
    }, store) => {
        const { dispatch } = store;

        // Not applying to Recorder -> For integration customers we are allowing them to set their video quality resolution via customResolution config parameter.
        // This will override the user preferred video quality setting.
        //
        // userInitiated is false on purpose: the slider gates the locked UI on customResolution being present
        // in config (see VideoQualitySlider.{web,native}.js), not on preferredVideoQualityUserInitiated, so
        // marking this as user-initiated would let the user unlock the slider on the next dispatch by going
        // through it once. Keeping it as a non-user override also keeps the participant-count cap and other
        // branches from interpreting the locked value as a sticky user choice.
        if (!config?.iAmRecorder && config?.interfaceConfig?.applyMeetingSettings === true && typeof config?.customResolution !== 'undefined'
            && config?.customResolution !== null && Number.isInteger(config.customResolution)) {
            if (config.customResolution === VIDEO_QUALITY_LEVELS.LOW || config.customResolution === VIDEO_QUALITY_LEVELS.MEDIUM
                || config.customResolution === VIDEO_QUALITY_LEVELS.STANDARD || config.customResolution === VIDEO_QUALITY_LEVELS.HIGH) {
                if (maxReceiverVideoQuality !== config.customResolution) {
                    dispatch(setMaxReceiverVideoQuality(config.customResolution));
                    dispatch(setPreferredVideoQuality(config.customResolution, false));
                }
            }
        } else if (preferredVideoQualityUserInitiated
                && preferredVideoQuality
                && preferredVideoQuality !== VIDEO_QUALITY_LEVELS.AUTO) {

            // A quality the user picked themselves outranks the participant-count cap below. The receiver
            // constraint is Math.min(preferredVideoQuality, maxReceiverVideoQuality), so leaving the cap in charge
            // silently overrode the choice - _adjustVideoQualityBasedOnTiles returns LOW (240) above ten
            // participants, so picking SD (480) in a busy call was floored to 240 and the label read LD.
            //
            // Gated on provenance rather than on "not AUTO": React Native dispatches
            // setPreferredVideoQuality(STANDARD) by itself on every CONFERENCE_JOINED, so matching on the value
            // alone read that automatic default as a user decision and switched the adaptive cap off across the
            // whole mobile platform - pinning it at 480 regardless of call size, the opposite of what the cap
            // exists to do for mobile. The cap still governs AUTO, which is the default everywhere else.
            if (maxReceiverVideoQuality !== preferredVideoQuality) {
                dispatch(setMaxReceiverVideoQuality(preferredVideoQuality));
            }
        } else if (userPreferredQuality && userPreferredQuality.persistedPrefferedVideoQuality !== VIDEO_QUALITY_LEVELS.AUTO) {
            if (maxReceiverVideoQuality !== userPreferredQuality.persistedPrefferedVideoQuality) {
                dispatch(setMaxReceiverVideoQuality(userPreferredQuality.persistedPrefferedVideoQuality));
            }
        } else {
            const newQuality = _adjustVideoQualityBasedOnTiles(finalParticipantCount, config, pregData);

            if (maxReceiverVideoQuality !== newQuality) {
                dispatch(setMaxReceiverVideoQuality(newQuality));
            }
        }
    },

    // The selector builds a new object every time, so without this the listener ran on every redux action.
    {
        deepEquals: true
    }
);

/**
 * Adjusts the video quality based on the number of visible tiles (participants).
 *
 * @param {number} participantCount - The number of participants in the conference.
 * @param {Object} config - The application configuration object.
 * @returns {number} The appropriate video quality level from VIDEO_QUALITY_LEVELS.
 */
export function _adjustVideoQualityBasedOnTiles(participantCount, config = {}, pregData = {}) {
    const {
        tileViewQualityThresholds = {
            ultraQualityMaxParticipants: 1,
            highQualityMaxParticipants: 4,
            standardQualityMaxParticipants: 6,
            mediumQualityMaxParticipants: 10,
            lowQualityMaxParticipants: 15,
            verylowQualityMaxParticipants: 20
        }
    } = config;

    const maxFullResolutionParticipants = config?.maxFullResolutionParticipants || 4;

    // For Recording & LiveStreaming Case - i want to keep video quality to High Quality when participants are hidden upto 20 participants.
    if (config?.iAmRecorder === true && participantCount <= tileViewQualityThresholds.verylowQualityMaxParticipants) {
        if (interfaceConfig?.hideParticipantTilesOnRecording && typeof pregData?.set_recording_tile_view !== 'undefined' && pregData?.set_recording_tile_view === false) {
            return VIDEO_QUALITY_LEVELS.HIGH;
        }
    }

    // For Recording & LiveStreaming Case - i want to keep video quality to High Quality when participants is less than 6.
    if (config?.iAmRecorder === true && participantCount <= tileViewQualityThresholds.standardQualityMaxParticipants) {
        if (typeof pregData?.set_recording_tile_view !== 'undefined' && pregData?.set_recording_tile_view === false) {
            return VIDEO_QUALITY_LEVELS.HIGH;
        }
    }

    // For Recording & LiveStreaming Case - i want to keep video quality to Standard Quality for 7 and above number of participants.
    if (config?.iAmRecorder === true && participantCount > tileViewQualityThresholds.standardQualityMaxParticipants) {
        if (typeof pregData?.set_recording_tile_view !== 'undefined' && pregData?.set_recording_tile_view === false) {
            return VIDEO_QUALITY_LEVELS.STANDARD;
        }
    }

    // Adjust for mobile browsers & Mobile Apps (React Native)
    if ((isMobileBrowser() || checkIfReactNative()) && participantCount <= tileViewQualityThresholds.standardQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.MEDIUM;
    } else if ((isMobileBrowser() || checkIfReactNative()) && participantCount > tileViewQualityThresholds.standardQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.LOW;
    }

    // Adjust for Desktop Browsers & Desktop Apps
    if (participantCount <= tileViewQualityThresholds.ultraQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.AUTO;
    } else if (participantCount <= maxFullResolutionParticipants || participantCount <= tileViewQualityThresholds.highQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.HIGH;
    } else if (participantCount <= tileViewQualityThresholds.standardQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.STANDARD;
    } else if (participantCount <= tileViewQualityThresholds.mediumQualityMaxParticipants) {
        return VIDEO_QUALITY_LEVELS.MEDIUM;
    }

    return VIDEO_QUALITY_LEVELS.LOW;
}

